import React from 'react';
import { Link } from 'react-router-dom';
import OutfitAdminLayout from './OutfitAdminLayout';

function OutfitAdminGetStarted({ email, onLogout }) {
  return (
    <OutfitAdminLayout
      email={email}
      onLogout={onLogout}
      pageTitle="Get Started"
    >
      <div className="outfit-gs-root">
        {/* Intro Card */}
        <div className="outfit-card">
          <div className="outfit-card-header">
            <div>
              <h2 className="outfit-card-title">Outfit Studio Overview</h2>
              <p className="outfit-gs-subtitle">
                A guide for stylists and merchandisers on managing fashion rules, category formulas, and trend collections.
              </p>
            </div>
            <div className="outfit-gs-header-actions">
              <Link to="/outfits/keywords-blacklist" className="outfit-btn sm">
                Keywords Blacklist
              </Link>
              <Link to="/outfits/cci-blacklist" className="outfit-btn sm">
                CCI Blacklist
              </Link>
              <Link to="/outfits/trends" className="outfit-btn primary sm">
                Trend Builder
              </Link>
            </div>
          </div>
          <div className="outfit-card-body" style={{ paddingTop: '8px' }}>
            <p className="outfit-gs-intro-text">
              Outfit Studio provides editorial control over how outfits are assembled, filtered, and surfaced to users across Polopan.
              Every rule configured here directly impacts AI outfit generation, search indexes, and shopping feeds in real time.
            </p>
          </div>
        </div>

        {/* 3 Pillars Grid */}
        <div className="outfit-card">
          <div className="outfit-card-header">
            <div>
              <h2 className="outfit-card-title">The 3 Core Modules</h2>
              <span className="outfit-card-subtitle">
                How each tool works and what it affects in the system.
              </span>
            </div>
          </div>
          <div className="outfit-card-body">
            <div className="outfit-gs-pillars-grid">
              {/* Pillar 1 */}
              <div className="outfit-gs-pillar-item">
                <div className="outfit-gs-pillar-top">
                  <span className="outfit-badge">Module 1</span>
                  <h3 className="outfit-gs-pillar-name">Keywords Blacklist</h3>
                </div>
                <p className="outfit-gs-pillar-desc">
                  Filter out specific visual property combinations that are off-trend or outdated.
                </p>

                <div className="outfit-gs-block">
                  <span className="outfit-gs-label">Stylist Action</span>
                  <p>
                    Select Gender, Super Category & Category, then select at most one value per property dimension
                    (e.g., <em>Fit: Skinny Fit</em> + <em>Look: Distressed</em> for Men's Jeans).
                  </p>
                </div>

                <div className="outfit-gs-block impact">
                  <span className="outfit-gs-label">API & Platform Impact</span>
                  <p>
                    Saves to <code>admin_keyword_blacklist</code>. Matching items are stripped from the search inverted index,
                    preventing them from appearing in generated outfits.
                  </p>
                </div>

                <div className="outfit-gs-pillar-footer">
                  <Link to="/outfits/keywords-blacklist" className="outfit-btn outline sm">
                    Open Keywords Blacklist &rarr;
                  </Link>
                </div>
              </div>

              {/* Pillar 2 */}
              <div className="outfit-gs-pillar-item">
                <div className="outfit-gs-pillar-top">
                  <span className="outfit-badge">Module 2</span>
                  <h3 className="outfit-gs-pillar-name">CCI Blacklist</h3>
                </div>
                <p className="outfit-gs-pillar-desc">
                  Govern entire category pairing formulas (e.g., Top Wear + Bottom Wear + Footwear).
                </p>

                <div className="outfit-gs-block">
                  <span className="outfit-gs-label">Stylist Action</span>
                  <p>
                    Review category combinations across Occasions. If an overall pairing doesn't work stylistically,
                    toggle its status to <strong>Discontinued</strong>.
                  </p>
                </div>

                <div className="outfit-gs-block impact">
                  <span className="outfit-gs-label">API & Platform Impact</span>
                  <p>
                    Saves to <code>admin_cci_blacklist</code>. The search engine applies an instant $O(1)$ mask to hide all look templates
                    matching that Category Combination ID.
                  </p>
                </div>

                <div className="outfit-gs-pillar-footer">
                  <Link to="/outfits/cci-blacklist" className="outfit-btn outline sm">
                    Open CCI Blacklist &rarr;
                  </Link>
                </div>
              </div>

              {/* Pillar 3 */}
              <div className="outfit-gs-pillar-item">
                <div className="outfit-gs-pillar-top">
                  <span className="outfit-badge">Module 3</span>
                  <h3 className="outfit-gs-pillar-name">Trend Builder</h3>
                </div>
                <p className="outfit-gs-pillar-desc">
                  Curate seasonal editorial collections and micro-trends with live inventory checking.
                </p>

                <div className="outfit-gs-block">
                  <span className="outfit-gs-label">Stylist Action</span>
                  <p>
                    Use the 4-step wizard to choose a base formula, pick visual facets with dynamic graying-out,
                    and publish the trend.
                  </p>
                </div>

                <div className="outfit-gs-block impact">
                  <span className="outfit-gs-label">API & Platform Impact</span>
                  <p>
                    Saves to <code>admin_trendy_outfits</code>. Powers dedicated trend carousels and hydrated shopping feeds
                    with real-time product catalogs.
                  </p>
                </div>

                <div className="outfit-gs-pillar-footer">
                  <Link to="/outfits/trends" className="outfit-btn outline sm">
                    Open Trend Builder &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Workflow Guide */}
        <div className="outfit-card">
          <div className="outfit-card-header">
            <div>
              <h2 className="outfit-card-title">Recommended Curation Workflow</h2>
              <span className="outfit-card-subtitle">
                A 3-step process for maintaining clean styling standards.
              </span>
            </div>
          </div>
          <div className="outfit-card-body">
            <div className="outfit-gs-workflow-list">
              <div className="outfit-gs-workflow-step">
                <div className="outfit-gs-step-num">1</div>
                <div className="outfit-gs-step-info">
                  <h4>Clean Catalog Aesthetics (Keywords Blacklist)</h4>
                  <p>
                    Eliminate undesirable cuts or washes within specific categories to prevent the AI from generating unflattering combinations.
                  </p>
                </div>
              </div>

              <div className="outfit-gs-workflow-step">
                <div className="outfit-gs-step-num">2</div>
                <div className="outfit-gs-step-info">
                  <h4>Audit Category Formulas (CCI Blacklist)</h4>
                  <p>
                    Filter formulas by Occasion and discontinue combinations where the category pairing is mismatched.
                  </p>
                </div>
              </div>

              <div className="outfit-gs-workflow-step">
                <div className="outfit-gs-step-num">3</div>
                <div className="outfit-gs-step-info">
                  <h4>Publish Curated Drops (Trend Builder)</h4>
                  <p>
                    Create targeted aesthetic drops. The real-time graying-out engine ensures only valid, high-inventory facet combinations are published.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pro Tips / FAQs */}
        <div className="outfit-card">
          <div className="outfit-card-header">
            <div>
              <h2 className="outfit-card-title">Key Concepts & FAQs</h2>
              <span className="outfit-card-subtitle">
                Common questions about platform behavior and updates.
              </span>
            </div>
          </div>
          <div className="outfit-card-body">
            <div className="outfit-gs-faq-grid">
              <div className="outfit-gs-faq-item">
                <h4>Why are some facet options disabled in Step 3 of Trend Builder?</h4>
                <p>
                  The co-occurrence graph evaluates active product inventory across all selected category slots.
                  If choosing a facet would result in zero matching combinations with your other selections, it is disabled to avoid creating empty trends.
                </p>
              </div>

              <div className="outfit-gs-faq-item">
                <h4>When do changes take effect in the user app?</h4>
                <p>
                  Updates to the <strong>Keywords Blacklist</strong> and <strong>CCI Blacklist</strong> persist directly to MongoDB and are applied to the search index with zero propagation delay.
                </p>
              </div>

              <div className="outfit-gs-faq-item">
                <h4>Can I preview hydrated looks for a trend?</h4>
                <p>
                  Yes. In the Trend Builder library, click <strong>"View Looks Feed"</strong> on any trend card to open the hydrated viewer displaying product images, prices, brands, and styling compositions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OutfitAdminLayout>
  );
}

export default OutfitAdminGetStarted;
